/**
 * Button primitive. shadcn-style — owned in-repo (not pinned to an npm
 * version), variants via cva, themed by Tailwind classes that resolve through
 * the @funkihoo/theme preset.
 */

import { type VariantProps, cva } from 'class-variance-authority';
import type { ButtonHTMLAttributes, ForwardedRef } from 'react';
import { forwardRef } from 'react';
import { cn } from '../lib/cn.js';

const buttonVariants = cva(
  [
    // Base
    'inline-flex items-center justify-center gap-2 whitespace-nowrap',
    'rounded-md text-sm font-medium',
    'transition-colors duration-base ease-standard',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600',
    'disabled:pointer-events-none disabled:opacity-50',
  ].join(' '),
  {
    variants: {
      variant: {
        primary: 'bg-primary-600 text-white hover:bg-primary-700',
        secondary: 'bg-neutral-100 text-neutral-900 hover:bg-neutral-200',
        outline: 'border border-neutral-300 bg-transparent text-neutral-900 hover:bg-neutral-50',
        ghost: 'bg-transparent text-neutral-900 hover:bg-neutral-100',
        danger: 'bg-danger-500 text-white hover:bg-danger-600',
        // outline-danger: a less-aggressive destructive look. Used by
        // confirm-then-do CTAs where the action is destructive but the
        // button isn't the primary affordance of the row (e.g. End-share,
        // Clear-history). Filled `danger` is for the actual destructive
        // commit.
        'outline-danger': 'border border-danger-100 bg-white text-danger-700 hover:bg-danger-50',
        // outline-primary: brand-colored outline. Maps the previous
        // hand-rolled `border-primary-200 bg-white text-primary-700 hover:bg-primary-50`
        // sites (preview-as-others-see-you, save-prefs, etc.).
        'outline-primary':
          'border border-primary-200 bg-white text-primary-700 hover:bg-primary-50',
        warning: 'border border-warning-200 bg-warning-50 text-warning-800 hover:bg-warning-100',
        link: 'text-primary-600 underline-offset-4 hover:underline',
        // link-quiet: text-only dismiss / "Not now" / "Got it" / "Skip"
        // — no bg, no border, no underline-on-hover. The standardised
        // shape for tertiary text actions that used to be hand-rolled
        // `text-neutral-500 text-xs hover:text-neutral-700`.
        'link-quiet': 'bg-transparent text-neutral-500 hover:text-neutral-700',
      },
      size: {
        sm: 'h-8 px-3 text-xs',
        md: 'h-10 px-4',
        lg: 'h-12 px-6 text-base',
        icon: 'h-10 w-10',
        // Smaller icon variants. Many overlay close-buttons + kebab
        // triggers + carousel chevrons across the SPA use 28/32/36px
        // square footprints; standardising the sizes here so the same
        // primitive covers them.
        'icon-sm': 'h-7 w-7',
        'icon-md': 'h-9 w-9',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
    },
  },
);

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof buttonVariants>;

export const Button = forwardRef(function Button(
  { className, variant, size, type = 'button', ...rest }: ButtonProps,
  ref: ForwardedRef<HTMLButtonElement>,
) {
  return (
    <button
      ref={ref}
      type={type}
      className={cn(buttonVariants({ variant, size }), className)}
      {...rest}
    />
  );
});

export { buttonVariants };
